import InputShortText from "../InputShortText/InputShortText";
import TextArea from "../TextArea/TextArea";
import { useTranslation } from "react-i18next";
import SectionTitle from "../SectionTitle/SectionTitle";
import { Meeting } from "../../api/MeetingsAPI";
import MeetingsAPI from "../../api/MeetingsAPI";
import SelectInput from "../SelectInput/SelectInput";
import CONFIG from "../../config/config";
import { MeetingType, meetingTypeToTranslationKey } from "../../shared/meetingTypeToTranslationKey";
import { shouldShowReminderCheckbox } from "../../shared/meetingReminderUtils";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import { useFormValidation } from "../../hooks/useFormValidation";
import { isFutureDate, isValidEmail, isValidPhone, isValidText } from "../../shared/validation";
import React, { useState } from "react";
import useSessionStorage from "../../hooks/useSessionStorage";
import IconButton from "../IconButton/IconButton";
import { Icon } from "../Icon/Icon";
import copy from "../../assets/images/copy.svg";
import cut from "../../assets/images/cut.svg";
import paste from "../../assets/images/paste.svg";
import Tooltip from "../Tooltip/Tooltip";
import { addMinutes } from "date-fns";
import { ResourceAction, ResourceActionType } from "../../shared/resourceAction.type";
import { toastService } from "../../services/ToastService";
import SmallCheckBox from "../SmallCheckBox/SmallCheckBox";
import { PermissionFlags } from "../PermissionFlags/PermissionFlags";
import PhoneNumberInput from "../PhoneNumberInput/PhoneNumberInput";
import CustomerInput from "../CustomerInput/CustomerInput";
import { Customer } from "../../api/CustomersAPI";
import { UserX, UserCheck, ExternalLink } from "lucide-react";
import { Modal } from "../Modal/Modal";
import CustomerForm from "../CustomerForm/CustomerForm";
import ActionLinkButton from "../ActionLinkButton/ActionLinkButton";
import Toggle from "../Toggle/Toggle";
import ExpandableSection from "../ExpandableSection/ExpandableSection";
import PlainTextButton from "../PlainTextButton/PlainTextButton";

type Props = {
  meeting: Meeting | null,
  onMeetingChange?: (value: Meeting) => void,
  isDisabled?: boolean,
  actionButton?: JSX.Element,
  timeZone?: string,
  onViewBookingDetails?: () => void;
};

const getInitialState = (meeting: Meeting | null) => {
  if (meeting) {
    return {
      customerName: isValidText(meeting.customerName, 2),
    };
  }
};

function MeetingForm({ meeting, onMeetingChange = () => null, isDisabled = false, actionButton, timeZone, onViewBookingDetails }: Props) {

  const { t } = useTranslation();
  const requiredFields = ['customerName'];
  const initialState = getInitialState(meeting);
  const { updateFieldValidation, isFormValid } = useFormValidation(requiredFields, initialState);
  const [copiedMeeting, setCopiedMeeting] = useSessionStorage<ResourceAction<Meeting> | null>('copiedResource', null);
  const [isTogglingNoShow, setIsTogglingNoShow] = useState(false);
  const [isCustomerModalVisible, setIsCustomerModalVisible] = useState(false);
  const [customerDetails, setCustomerDetails] = useState<Customer | null>(null);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(false);

  const registerAction = (action: ResourceActionType) => {
    displayMessageFeedback(action);
    setCopiedMeeting({ action: action, resource: meeting! });
  }

  const displayMessageFeedback = (action: ResourceActionType) => {
    if (action === "copy") {
      toastService.showToast(t("global.already_copy"));
    } else {
      toastService.showToast(t("global.already_cut"));
    }
  }

  const handleCut = () => {
    if (meeting) registerAction('cut');
  };

  const handleCopy = () => {
    if (meeting) registerAction('copy');
  };

  const handleMeetingChange = (updatedMeeting: Meeting) => {
    onMeetingChange(updatedMeeting);
    updateFieldValidation('customerName', isValidText(updatedMeeting.customerName, 2));
  };  

  const sanitizeMeetingForPaste = (resource: Meeting): Meeting => {
    const { reservationId, bookedMeetingType, inboundChannel, ...rest } = resource as any;
    return rest as Meeting;
  };

  const handlePaste = () => {
    if (copiedMeeting) {
      if (copiedMeeting.action === 'copy') {
        const sanitized = sanitizeMeetingForPaste(copiedMeeting.resource);
        handleMeetingChange({
          ...sanitized,
          id: meeting?.id,
          startTime: meeting?.startTime,
          endTime: meeting?.endTime,
        } as Meeting);
      } else if (copiedMeeting.action === 'cut') {
        handleMeetingChange({
          ...copiedMeeting.resource,
          startTime: meeting?.startTime,
          endTime: meeting?.endTime,
        } as Meeting);
      } else {
        console.error("Could not execute action on resource!");
      }
    }
  };

  const disabledActionButton = actionButton
    ? React.cloneElement(
        actionButton,
        {},
        React.Children.map(actionButton.props.children, (child) => {
          return child ? React.cloneElement(child, { isDisabled: !isFormValid() }) : null;
        })
      )
    : null;

  const updateMeetingTimes = (start: Date) => {
    const end = addMinutes(start, 60);
    onMeetingChange({...meeting, startTime: start.toISOString(), endTime: end.toISOString() } as Meeting);
  };

  const fillCustomerData = (customer: Customer) => {
    onMeetingChange(
      {...meeting, 
        customerName: customer.name, 
        customerSurname: customer.surname,
        customer: customer,
        phone: customer?.phone,
        email: customer?.email
      } as Meeting);
  }

  const handleToggleNoShow = async () => {
    if (!meeting?.id || isTogglingNoShow) return;
    
    setIsTogglingNoShow(true);
    try {
      const response = await MeetingsAPI.markNoShow(meeting.id);
      
      onMeetingChange({
        ...meeting,
        noShow: response.data.noShow
      } as Meeting);
      
      toastService.showToast(
        response.data.noShow 
          ? t("calendar.meeting.form.marked_noshow", "Meeting marked as no-show")
          : t("calendar.meeting.form.unmarked_noshow", "Meeting unmarked as no-show")
      );
    } catch (error) {
      console.error('Failed to toggle no-show status:', error);
      toastService.showToast(t("toast.errors.generic", "An error occurred"));
    } finally {
      setIsTogglingNoShow(false);
    }
  };

  const handleViewCustomerDetails = async () => {
    if (!meeting?.customerId) return;
    
    setIsLoadingCustomer(true);
    try {
      const { default: CustomersAPI } = await import('../../api/CustomersAPI');
      
      const response = await CustomersAPI.getById(meeting.customerId.toString());
      setCustomerDetails(response.data);
      setIsCustomerModalVisible(true);
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      toastService.showToast(t("toast.errors.generic", "Failed to load customer details"));
    } finally {
      setIsLoadingCustomer(false);
    }
  };

  const closeCustomerModal = () => {
    setIsCustomerModalVisible(false);
    setCustomerDetails(null);
  };

  return (
    <>
      <div>
        <PermissionFlags requiredFlags={['create-meetings']}>
          <div className="flex justify-between">
            <div className="flex items-center">
              {onViewBookingDetails && (
                <ActionLinkButton
                  text={t("bookings.details.view_receipt") || "See reservation details"}
                  onClick={onViewBookingDetails}
                  actionName="View Reservation Details"
                  icon={<ExternalLink className="h-4 w-4" />}
                />
              )}
            </div>
            <div className="flex justify-end">
              {(!isDisabled && copiedMeeting) && (
                <Tooltip content={t("global.paste")} position="bottom">
                  <IconButton onClick={handlePaste} disabled={isDisabled || !copiedMeeting}>
                    <Icon url={paste} cssStyles="w-5"/>
                  </IconButton>
                </Tooltip>
              )} 
              <Tooltip content={t("global.cut")} position="bottom">
                <IconButton onClick={handleCut}>
                  <Icon url={cut} cssStyles="w-5"/>
                </IconButton>
              </Tooltip>
              <Tooltip content={t("global.copy")} position="bottom">
                <IconButton onClick={handleCopy}>
                  <Icon url={copy} cssStyles="w-5"/>
                </IconButton>
              </Tooltip>
              {meeting?.id && (
                <Tooltip content={meeting.noShow 
                  ? t("calendar.meeting.form.unmark_noshow", "Unmark as No-Show")
                  : t("calendar.meeting.form.mark_noshow", "Mark as No-Show")
                } position="bottom">
                  <div className={meeting.noShow ? "text-red-600 hover:text-red-700" : "text-gray-600 hover:text-gray-700"}>
                    <IconButton 
                      onClick={handleToggleNoShow}
                      disabled={isDisabled || isTogglingNoShow}
                    >
                      {meeting.noShow ? <UserX className="w-5 h-5" /> : <UserCheck className="w-5 h-5" />}
                    </IconButton>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </PermissionFlags>
        <div className="mt-4"></div>
        <div className="flex flex-wrap mb-2">
          <SelectInput values={CONFIG.MEETINGS.STATUS.map(e => { return { value: e, readableValue: t(meetingTypeToTranslationKey(e as MeetingType)) } })}
            onChange={e => onMeetingChange({ ...meeting, type: e.currentTarget.value } as Meeting)}
            label={"calendar.meeting.form.type"}
            defaultValue={meeting?.type || 'VISIT'}
            isDisabled={isDisabled}
          />
        </div>
        <div className="flex flex-wrap space-x-2">
          <div className="flex-1">
            <CustomerInput
              onCustomerSelect={c => fillCustomerData(c)}
              onValidationChange={(isValid) => updateFieldValidation('customerName', isValid)}
              label={"calendar.meeting.form.name"}
              onChange={e => onMeetingChange({ ...meeting, customerName: e.currentTarget.value } as Meeting)}
              errorMessage={"errors.too_short"}
              defaultValue={meeting?.customer?.name || meeting?.customerName || ''}
              isDisabled={isDisabled}
              // validationFn={(value) => !!value && isValidText(value, 2)}
            />
            {meeting?.customerId && (
              <div className="mt-2">
                <ActionLinkButton
                  text={isLoadingCustomer ? t("global.loading", "Loading...") : t("calendar.meeting.form.view_customer")}
                  onClick={handleViewCustomerDetails}
                  disabled={isDisabled || isLoadingCustomer}
                  actionName="View Customer Details"
                  icon={<ExternalLink className="h-4 w-4" />}
                />
              </div>
            )}
          </div>
          { !meeting?.customer && 
            <InputShortText
              label={"calendar.meeting.form.surname"}
              validationFn={(value) => !!value && isValidText(value, 2)}
              onChange={e => onMeetingChange({ ...meeting, customerSurname: e.currentTarget.value } as Meeting)}
              errorMessage={"errors.too_short"}
              defaultValue={meeting?.customer?.surname || meeting?.customerSurname || ''}
              isDisabled={isDisabled}
            />
          }
        </div>
      </div>
      <br/>
      <div className="flex flex-wrap space-x-5">
        <CustomDatePicker
          validationFn={(value) => !!value && isFutureDate(value)}
          errorMessage={"errors.past_date"}
          dateFormat="dd/MM/yyyy HH:mm"
          label="calendar.meeting.form.start"
          value={meeting?.startTime}
          onChange={c => updateMeetingTimes(c)}
          showTimeSelect
          isDisabled={isDisabled}
          timeZone={timeZone}
        />
        <CustomDatePicker
          dateFormat="dd/MM/yyyy HH:mm"
          label="calendar.meeting.form.end"
          validationFn={(value) => !!value && isFutureDate(value)}
          errorMessage={"errors.past_date"}
          value={meeting?.endTime}
          onChange={c => onMeetingChange({...meeting, endTime: c!.toISOString() } as Meeting)}
          showTimeSelect
          isDisabled={isDisabled}
          timeZone={timeZone}
        />
      </div>
      <br/>
      <SectionTitle text={t("admin.form.other_data")} />
      <div className="flex flex-wrap space-x-5">
        <CustomDatePicker
          value={meeting?.weddingDate}
          onChange={e => onMeetingChange({ ...meeting, weddingDate: e.toISOString() } as Meeting)}
          label={"calendar.meeting.form.wedding_date"}
          dateFormat="LL/yyyy"
          isDisabled={isDisabled}
          timeZone={timeZone}
        />
        <InputShortText
          label={"calendar.meeting.form.email"}
          validationFn={(value) => !!value && isValidEmail(value)}
          onChange={e => onMeetingChange({ ...meeting, email: e.currentTarget.value } as Meeting)}
          errorMessage={"errors.invalid_email"}
          defaultValue={meeting?.customer?.email || meeting?.email || ''}
          isDisabled={isDisabled}
        />
      </div>
      <div>
        <div className="flex flex-wrap space-x-5">
          <PhoneNumberInput 
            validationFn={(value) => !!value && isValidPhone(value)} 
            label={"calendar.meeting.form.phone"}
            onChange={e => onMeetingChange({ ...meeting, phone: e } as Meeting)}
            defaultValue={meeting?.customer?.phone || meeting?.phone || ''}
            isDisabled={isDisabled}
          />
        </div>
        { meeting?.type && shouldShowReminderCheckbox(meeting.type) && 
        <div className="flex flex-wrap">
          <SmallCheckBox
            label={"calendar.meeting.form.phone.reminder"}
            isDisabled={isDisabled}
            defaultValue={meeting?.reminder}
            onChange={e => 
              onMeetingChange({ ...meeting, reminder: Boolean(e.currentTarget.checked) } as Meeting)}
          />
        </div>
        }
        <TextArea
          label={"calendar.meeting.form.notes"}
          validationFn={(value) => !value || (!!value &&  isValidText(value, 0, 1000))}
          onChange={e => {
            const trimmedValue = e.currentTarget.value.trim();
            onMeetingChange({ ...meeting, notes: trimmedValue } as Meeting);
          }}
          errorMessage={"errors.too_long"}
          defaultValue={meeting?.notes || ''}
          isDisabled={isDisabled}
          maxLength={1000}
        />
        <div className="my-4">
          <ExpandableSection
            defaultState={showAdditionalSettings}
            expandedContent={
              <div className="space-y-4 p-3 border rounded-lg bg-gray-50">
                <div className="text-sm text-muted-foreground">
                  {t("calendar.meeting.form.more_settings.hint") || "Advanced settings for this meeting."}
                </div>
                <div className="flex items-start justify-between">
                  <div className="pr-4">
                    <div className="text-sm font-medium">
                      {t("calendar.meeting.form.exclusive") || "Exclusive"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {t("calendar.meeting.form.exclusive.hint") || "When enabled, no other appointments can overlap this time."}
                    </div>
                  </div>
                  <Toggle
                    label={t("calendar.meeting.form.exclusive") || "Exclusive"}
                    defaultValue={Boolean(meeting?.exclusive)}
                    isDisabled={isDisabled}
                    onChange={(value: boolean) => onMeetingChange({ ...meeting, exclusive: value } as Meeting)}
                  />
                </div>
              </div>
            }
            collapsedContent={
              <div className="inline-flex">
                <PlainTextButton 
                  text={t("calendar.meeting.form.more_settings") || "More settings"}
                  onClick={() => setShowAdditionalSettings(prev => !prev)}
                  actionName="Expand Meeting More Settings"
                />
              </div>
            }
          />
        </div>
      </div>
      
      {/* Mobile-only */}
      {disabledActionButton && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10">
          <div className="max-w-7xl mx-auto">
            {disabledActionButton}
          </div>
        </div>
      )}
      
      {/* Desktop action button */}
      <div className="hidden md:block">
        {disabledActionButton}
      </div>

      {isLoadingCustomer && (
        <Modal
          modalTitle={t("global.loading", "Loading...")}
          canClose={false}
        >
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">{t("customers.details.loading")}</p>
          </div>
        </Modal>
      )}

      {isCustomerModalVisible && customerDetails && !isLoadingCustomer && (
        <Modal
          modalTitle={t("customers.details.title")}
          canClose
          onClose={closeCustomerModal}
        >
          <div className="max-h-[80vh] overflow-y-auto">
            <CustomerForm
              customer={customerDetails}
              isDisabled={true}
            />
          </div>
        </Modal>
      )}
    </>
  );
}

export default MeetingForm;
